import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Fraud from '../blog/Fraud'; // Component for "blog-fraud"
import AssuranceMapping from '../blog/AssuranceMapping'; // Component for "blog-integrated-assurance"
import DigitalTransformationDashboard from '../blog/DigitalTransformationDashboard'; // Component for "
import ProjectDashboard from '../blog/ProjectDashboard'; // Component for "blog-project-dashboard"
// Import additional blog components as needed

const blogPosts = [
  {
    id: 1,
    slug: "blog-fraud",
    title: "Visualising Fraud Risk Indicators",
    summary:
      "Explore how data visualization and filtering help identify fraud risks with interactive charts and detailed incident reports.",
    content:
      "In this blog post, we dive deep into how organizations can leverage data visualization to monitor fraud risks. We cover various strategies, including dynamic filtering of incident data and detailed breakdowns of risk factors. The interactive report below demonstrates how the system works, including metrics like the number of incidents, frequency, and detailed incident breakdowns. This helps in understanding how the risk score is calculated and where preventive measures could be applied.",
    publishedDate: "2025-March-07", // Display date
    publishedAt: "2025-03-07",     // ISO date for filtering
    category: "Fraud",           // Category property
    component: Fraud,
  },
  {
    id: 2,
    slug: "blog-integrated-assurance",
    title: "Understanding Assurance Mapping",
    summary:
      "Discover a comprehensive guide to integrated assurance mapping, featuring a detailed checklist and a practical example to align assurance activities with strategic risk management.",
    content:
      "In this blog article, we explore how organizations can enhance their integrated assurance processes using a structured checklist and a real-world example. The post breaks down key steps such as defining objectives, mapping assurance activities, assigning roles, assessing maturity, and establishing KPIs. A practical case study featuring 'TechCorp' illustrates how these steps translate into effective assurance mapping, ensuring continuous improvement and better risk management.",
    publishedDate: "2025-March-08",
    publishedAt: "2025-03-08",
    category: "Assurance",
    component: AssuranceMapping,
  },

{
  id: 3,
  slug: "blog-project-dashboard",
  title: "Audit Project Dashboard: Metrics and Insights",
  summary:
    "Discover how an audit project dashboard can provide real-time metrics and insights to improve audit efficiency and effectiveness.",
  content:
    "In this blog post, we explore the benefits of using an audit project dashboard to track key metrics and gain valuable insights into audit processes. The post highlights features such as real-time status updates, risk heatmaps, resource allocation charts, and trend analysis tools. By leveraging these functionalities, audit teams can enhance their decision-making, streamline workflows, and optimize resource utilization. The interactive dashboard below showcases a sample project dashboard with various metrics and visualizations.",
  publishedDate: "2025-March-09",
  publishedAt: "2025-03-09",
  category: "Audit Management",
  component: ProjectDashboard,
}
  // Add more blog posts here as needed
];

const Blog = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [filterLastMonth, setFilterLastMonth] = useState(false);
  const [filterThisMonth, setFilterThisMonth] = useState(false);

  // Utility to get the date range for last month
  const getLastMonthRange = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const startDate = new Date(lastMonthYear, lastMonth, 1);
    const endDate = new Date(lastMonthYear, lastMonth + 1, 0);
    return { startDate, endDate };
  };

  // Utility to get the date range for this month
  const getThisMonthRange = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const startDate = new Date(currentYear, currentMonth, 1);
    const endDate = new Date(currentYear, currentMonth + 1, 0);
    return { startDate, endDate };
  };

  // Find the selected blog post by its slug if present
  const selectedPost = slug ? blogPosts.find((post) => post.slug === slug) : null;

  // Function to copy the current URL to clipboard for sharing
  const handleShare = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => alert("Link copied to clipboard!"))
      .catch(() => alert("Failed to copy link."));
  };

  // Filtering logic for blog posts
  const filteredPosts = blogPosts.filter((post) => {
    // Keyword search filter (checks title and summary)
    const keywordMatch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.summary.toLowerCase().includes(searchTerm.toLowerCase());

    // Category filter
    const categoryMatch = selectedCategory === "All" || post.category === selectedCategory;

    // Month filter: if neither filter is active, always true. Otherwise, check if the post's publishedAt date is in one of the selected ranges.
    let monthMatch = true;
    if (filterLastMonth || filterThisMonth) {
      monthMatch = false;
      const publishedDate = new Date(post.publishedAt);
      if (filterLastMonth) {
        const { startDate, endDate } = getLastMonthRange();
        if (publishedDate >= startDate && publishedDate <= endDate) {
          monthMatch = true;
        }
      }
      if (!monthMatch && filterThisMonth) {
        const { startDate, endDate } = getThisMonthRange();
        if (publishedDate >= startDate && publishedDate <= endDate) {
          monthMatch = true;
        }
      }
    }

    return keywordMatch && categoryMatch && monthMatch;
  });

  // If a blog post is selected, render its detailed view along with the share button
  if (selectedPost) {
    const BlogContent = selectedPost.component;

    return (
      <div className="p-4 md:p-8 bg-gray-50 rounded-lg shadow-md">
        <button
          onClick={() => navigate('/blog')}
          className="mb-4 text-blue-500 hover:underline focus:outline-none"
        >
          &larr; Back to Blog Summary
        </button>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {selectedPost.title}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Published on: {selectedPost.publishedDate} | Category: {selectedPost.category}
        </p>
        <p className="text-gray-700 mb-4">{selectedPost.content}</p>
        <button
          onClick={handleShare}
          className="mb-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
        >
          Share This Post
        </button>
        {/* Render the specific blog content component if available */}
        {BlogContent && <BlogContent />}
      </div>
    );
  }

  // Get list of unique categories from blog posts for the filter dropdown
  const categories = ["All", ...new Set(blogPosts.map((post) => post.category))];

  // Otherwise, render the list of blog summaries with filters
  return (
    <div className="p-4 md:p-8 bg-gray-50 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Blog Posts</h2>
      
      {/* Filter Bar */}
      <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {/* Last Month Filter */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filterLastMonth}
              onChange={(e) => setFilterLastMonth(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="text-sm text-gray-700">Published Last Month</span>
          </label>
          {/* This Month Filter */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filterThisMonth}
              onChange={(e) => setFilterThisMonth(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="text-sm text-gray-700">Published This Month</span>
          </label>
        </div>
        {/* Keyword Search */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search blog posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      
      {/* Blog Posts List */}
      <ul className="space-y-4">
        {filteredPosts.map((post) => (
          <li
            key={post.id}
            className="p-4 bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
          >
            <Link to={`/blog/${post.slug}`}>
              <h3 className="text-xl font-semibold text-gray-700">
                {post.title}
              </h3>
              <p className="text-gray-600">{post.summary}</p>
              <p className="text-sm text-gray-500">
                Published on: {post.publishedDate} | Category: {post.category}
              </p>
            </Link>
          </li>
        ))}
        {filteredPosts.length === 0 && (
          <li className="p-4 bg-white border border-gray-200 rounded-md text-center text-gray-500">
            No blog posts found.
          </li>
        )}
      </ul>
    </div>
  );
};

export default Blog;
